import { z } from 'zod';

/**
 * Pipefy card intake shared by ingress channels such as structured email and webhook (ALC-1470).
 *
 * A Pipefy automation may enter through the authored email template or the webhook adapter.
 * Both paths parse the same canonical representation and stamp this context into
 * `ApDocument.metadata.pipefyIntake` on every document it creates for the card, so the assignment
 * pipeline can enrich the resulting bill (ingestion metadata, requester, deterministic categoria /
 * departamento, mismatch + missing-documents alerts).
 */

/** Key under `ApDocument.metadata` carrying the intake context. */
export const PIPEFY_INTAKE_METADATA_KEY = 'pipefyIntake';

/** `PipefyCardIntakeReservation.source` discriminator for the external provider. */
export const PIPEFY_INTAKE_SOURCE = 'pipefy';

export interface PipefyCardIdVariants {
  original: string;
  normalized: string;
}

/**
 * Build the two representations used when correlating Pipefy data written by
 * different ingestion paths. Historical email intake rows may contain only
 * digits, while webhook payloads preserve the provider's textual value.
 */
export function pipefyCardIdVariants(value: string | number): PipefyCardIdVariants {
  const original = String(value).trim();
  const withoutSpaces = original.replace(/\s+/g, '');
  const numericShape = /\d/.test(original) && !/[A-Za-z]/.test(original);
  const normalized = numericShape ? withoutSpaces.replace(/\D/g, '') : withoutSpaces;
  return { original, normalized };
}

/**
 * Preserve the historical structured-intake representation. The email parser
 * has always accepted any digits-bearing value and persisted only its digits;
 * keeping that behavior avoids breaking existing intake deduplication.
 */
export function pipefyCardIdForStructuredIntake(value: string | number): string {
  return pipefyCardIdVariants(value).original.replace(/\D/g, '');
}

/**
 * Link that opens the card itself. Pipefy's `card.url` on webhook payloads points at the
 * pipe (`/pipes/<pipeId>`), so the card link is built from the card id instead.
 */
export function pipefyOpenCardUrl(cardId: string | number | null | undefined): string | null {
  if (cardId == null) return null;
  const id = pipefyCardIdForStructuredIntake(cardId);
  return id ? `https://app.pipefy.com/open-cards/${id}` : null;
}

/**
 * Line grammar of the authored Pipefy e-mail template (`x-alchemy-source: pipefy` plus
 * `key: value` lines). Owned here so the worker's parser and the API read the same contract.
 */
export const PIPEFY_EMAIL_SENTINEL_LINE = /^\s*x-alchemy-source\s*:\s*pipefy\s*$/i;
export const PIPEFY_EMAIL_KEY_VALUE_LINE = /^\s*([^:\s][^:]{0,59}?)\s*:(.*)$/;

/** Template key → canonical field key (`Pipefy Card ID` → `pipefy_card_id`). */
export function normalizePipefyEmailKey(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** True when the text has the `x-alchemy-source: pipefy` sentinel line. */
export function hasPipefyEmailSentinelLine(text: string | null | undefined): boolean {
  return (text ?? '').split(/\r?\n/).some((line) => PIPEFY_EMAIL_SENTINEL_LINE.test(line));
}

/**
 * The card id of a Pipefy template e-mail's `pipefy_card_id:` line, in the structured-intake
 * representation (digits only), or null when there is no digits-bearing line.
 */
export function pipefyEmailCardId(text: string | null | undefined): string | null {
  for (const line of (text ?? '').split(/\r?\n/)) {
    const match = PIPEFY_EMAIL_KEY_VALUE_LINE.exec(line);
    if (!match?.[1] || normalizePipefyEmailKey(match[1]) !== 'pipefy_card_id') continue;
    const id = pipefyCardIdForStructuredIntake((match[2] ?? '').trim());
    return id === '' ? null : id;
  }
  return null;
}

export function pipefyCardIdsEqual(left: string | number, right: string | number): boolean {
  const leftVariants = pipefyCardIdVariants(left);
  const rightVariants = pipefyCardIdVariants(right);
  if (!leftVariants.original || !rightVariants.original) return false;
  return (
    leftVariants.original === rightVariants.original ||
    leftVariants.normalized === rightVariants.normalized
  );
}

/**
 * Persisted reference to a Pipefy attachment URL. Signed query strings are never stored:
 * only host, path and a fingerprint of those two, plus the fetch-failure reason.
 */
export interface PipefyIntakeFetchFailure {
  host: string;
  path: string;
  fingerprint: string;
  reason: string;
}

export interface PipefyAttachmentRef {
  host: string;
  path: string;
  fingerprint: string;
}

/** FNV-1a 32-bit, portable across API/worker/web (no node:crypto). */
function fnv1aHex(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Strips the signed query from a Pipefy (or any https) attachment URL. Returns null when the
 * value is not an absolute URL. The fingerprint is of host+pathname only, so two signed URLs
 * for the same object collapse to the same ref.
 */
export function pipefyAttachmentRefFromUrl(url: string): PipefyAttachmentRef | null {
  try {
    const parsed = new URL(url);
    if (!parsed.protocol.startsWith('http')) return null;
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname || '/';
    return { host, path, fingerprint: fnv1aHex(`${host}${path}`) };
  } catch {
    return null;
  }
}

const EMBEDDED_HTTP_URL = /https?:\/\/[^\s"'\\]+/gi;

/** Origin + pathname, no query/hash — safe to persist as `sourceUrl` after a download. */
export function persistablePipefySourceUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (!parsed.protocol.startsWith('http')) return null;
    return `${parsed.origin}${parsed.pathname || '/'}`;
  } catch {
    return null;
  }
}

/**
 * Recursively strip query/hash from HTTP(S) URLs, including JSON-encoded attachment
 * field values (`["https://…?signature=…"]`). Live signed URLs stay on the in-memory
 * card `attachments` / field lists used by the fetcher.
 */
export function persistablePipefySnapshotValue(value: unknown): unknown {
  if (typeof value === 'string') {
    const asUrl = persistablePipefySourceUrl(value);
    if (asUrl) return asUrl;
    const trimmed = value.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        return JSON.stringify(persistablePipefySnapshotValue(JSON.parse(value)));
      } catch {
        // fall through to embedded-URL replacement
      }
    }
    return value.replace(EMBEDDED_HTTP_URL, (url) => persistablePipefySourceUrl(url) ?? url);
  }
  if (Array.isArray(value)) return value.map((item) => persistablePipefySnapshotValue(item));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        persistablePipefySnapshotValue(entry),
      ]),
    );
  }
  return value;
}

export function pipefyAttachmentFetchKey(url: string): string | null {
  const ref = pipefyAttachmentRefFromUrl(url);
  return ref ? `${ref.host}${ref.path}` : null;
}

/** Operator-facing `anexos:` line: path or host+path, never the signed query. */
export function describePipefyAttachmentForPersistence(
  url: string,
  storedPath?: string | null,
): string {
  const trimmedPath = storedPath?.replace(/^\/+/, '').trim();
  if (trimmedPath) return trimmedPath;
  const persistable = persistablePipefySourceUrl(url);
  if (persistable) {
    try {
      const parsed = new URL(persistable);
      return `${parsed.hostname}${parsed.pathname}`;
    } catch {
      return persistable;
    }
  }
  return 'attachment';
}

export function pipefyFetchFailureFromUrl(url: string, reason: string): PipefyIntakeFetchFailure {
  const ref = pipefyAttachmentRefFromUrl(url);
  if (ref) return { ...ref, reason };
  // Unparseable values (relative paths, missing scheme, broken host) must not persist raw
  // query material. Fingerprint the original string so the same bad URL still collapses.
  return { host: '', path: '', fingerprint: fnv1aHex(url), reason };
}

/**
 * Stable identity of a file read from a card's configured attachment field. Pipefy
 * signed download URLs expire within minutes, so the storage path — never the URL —
 * identifies the file across re-deliveries and re-fetches.
 */
export interface PipefyFieldAttachmentIdentity {
  cardId: string;
  fieldId: string;
  /** Pipefy storage path of the file. */
  attachmentPath: string;
}

export const PIPEFY_ATTACHMENT_FIELD_RECOVERY_OUTCOMES = [
  'RECOVERED',
  'FIELD_EMPTY',
  'CARD_NOT_FOUND',
  'DOWNLOAD_FAILED',
  'LOOKUP_FAILED',
] as const;
export type PipefyAttachmentFieldRecoveryOutcome =
  (typeof PIPEFY_ATTACHMENT_FIELD_RECOVERY_OUTCOMES)[number];

export interface PipefyRecoveredFieldAttachment extends PipefyFieldAttachmentIdentity {
  blake3: string;
}

export interface PipefyAttachmentFieldRecoveryFailure {
  attachmentPath: string;
  reason: string;
}

/**
 * Attempt to read the company's configured attachment field when the e-mail carried no
 * real file. Only recorded when an attachment field is configured; readers tolerate a
 * null `fieldId`.
 */
export interface PipefyAttachmentFieldRecovery {
  fieldId: string | null;
  /** All attachment fields read for this card; `fieldId` remains for legacy consumers. */
  fieldIds?: string[];
  outcome: PipefyAttachmentFieldRecoveryOutcome;
  recovered: PipefyRecoveredFieldAttachment[];
  failures: PipefyAttachmentFieldRecoveryFailure[];
  /** Sanitized provider/configuration error for `LOOKUP_FAILED`. */
  error?: string;
  /** Files the field listed beyond the per-card cap; they were not downloaded. */
  truncated?: number;
}

export interface PipefyDocumentIntake {
  source: typeof PIPEFY_INTAKE_SOURCE;
  /** Pipefy card id — the external reference used for intake-level dedup. */
  cardId: string;
  cardUrl: string | null;
  requesterName: string | null;
  requesterEmail: string | null;
  entityHint: string | null;
  entityRouting: Record<string, unknown> | null;
  vendorName: string | null;
  vendorTaxId: string | null;
  /** Card amount as stated in Pipefy. Documents win on financial truth; mismatches raise alerts. */
  amount: number | null;
  /**
   * Server-resolved ISO 4217 code: the card's supported `moeda`, or the entity
   * functional currency when `moeda` is blank. Absent while a currency review
   * is pending — that raw value lives on `currencyReview`, not here.
   */
  currency: string | null;
  /** Nonblank `moeda` as typed on the card, even when it is not an ISO code. */
  rawCurrency?: string | null;
  /** ISO yyyy-mm-dd. */
  dueDate: string | null;
  /** `forma_pagamento` as typed on the card (free text, not yet a PaymentMethodType). */
  paymentMethod: string | null;
  /**
   * `chave_pix` — the vendor's permanent PIX receiving key (CPF/CNPJ, e-mail, phone
   * or random), NOT a copia-e-cola payload. Assignment persists it as a
   * VendorBankAccount so the payment carries a real receiving method.
   */
  pixKey: string | null;
  /** Categoria code (e.g. "2.4.10.001"), used for deterministic segment-value resolution. */
  categoryCode: string | null;
  categoryLabel: string | null;
  /** Raw categoria field as typed in Pipefy ("2.4.10.001 Team building"). */
  categoryRaw: string | null;
  department: string | null;
  /** Raw values of the recognized template keys, for the bill's audit trail. */
  rawFields: Record<string, string>;
  /** Attachment downloads that failed (host/path/fingerprint only — never the signed URL). */
  attachmentFetchFailures: PipefyIntakeFetchFailure[];
  /** Channel that produced the structured input; absent on rows written before phase 6B (email). */
  channel?: 'EMAIL' | 'WEBHOOK';
  /** Absent on intakes that never consulted a configured attachment field. */
  attachmentFieldRecovery?: PipefyAttachmentFieldRecovery;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/**
 * Read the Pipefy intake context from an `ApDocument.metadata` value.
 * Returns null for documents that did not come from a Pipefy card.
 */
export function readPipefyIntakeFromDocumentMetadata(
  metadata: unknown,
): PipefyDocumentIntake | null {
  const record = asRecord(metadata);
  const raw = asRecord(record?.[PIPEFY_INTAKE_METADATA_KEY]);
  if (!raw) return null;
  if (raw.source !== PIPEFY_INTAKE_SOURCE) return null;
  if (typeof raw.cardId !== 'string' || !raw.cardId) return null;

  const rawFieldsRecord = asRecord(raw.rawFields);
  const rawFields: Record<string, string> = {};
  for (const [key, value] of Object.entries(rawFieldsRecord ?? {})) {
    if (typeof value === 'string') rawFields[key] = value;
  }

  const failures = Array.isArray(raw.attachmentFetchFailures)
    ? raw.attachmentFetchFailures
        .map((entry) => asRecord(entry))
        .filter((entry): entry is Record<string, unknown> => entry !== null)
        .flatMap((entry) => {
          if (typeof entry.reason !== 'string' || !entry.reason) return [];
          if (
            typeof entry.host === 'string' &&
            typeof entry.path === 'string' &&
            typeof entry.fingerprint === 'string' &&
            entry.host &&
            entry.path &&
            entry.fingerprint
          ) {
            return [
              {
                host: entry.host,
                path: entry.path,
                fingerprint: entry.fingerprint,
                reason: entry.reason,
              },
            ];
          }
          if (typeof entry.url === 'string' && entry.url) {
            return [pipefyFetchFailureFromUrl(entry.url, entry.reason)];
          }
          return [];
        })
    : [];

  const attachmentFieldRecovery = readPipefyAttachmentFieldRecovery(raw.attachmentFieldRecovery);

  return {
    source: PIPEFY_INTAKE_SOURCE,
    cardId: raw.cardId,
    cardUrl: optionalString(raw.cardUrl),
    requesterName: optionalString(raw.requesterName),
    requesterEmail: optionalString(raw.requesterEmail),
    entityHint: optionalString(raw.entityHint),
    entityRouting: asRecord(raw.entityRouting),
    vendorName: optionalString(raw.vendorName),
    vendorTaxId: optionalString(raw.vendorTaxId),
    amount: typeof raw.amount === 'number' && Number.isFinite(raw.amount) ? raw.amount : null,
    currency: optionalString(raw.currency),
    dueDate: optionalString(raw.dueDate),
    paymentMethod: optionalString(raw.paymentMethod),
    pixKey: optionalString(raw.pixKey),
    categoryCode: optionalString(raw.categoryCode),
    categoryLabel: optionalString(raw.categoryLabel),
    categoryRaw: optionalString(raw.categoryRaw),
    department: optionalString(raw.department),
    rawFields,
    attachmentFetchFailures: failures,
    ...(raw.channel === 'EMAIL' || raw.channel === 'WEBHOOK' ? { channel: raw.channel } : {}),
    ...(attachmentFieldRecovery ? { attachmentFieldRecovery } : {}),
  };
}

/** Read a stored attachment field recovery record; null when absent or not recognizable. */
export function readPipefyAttachmentFieldRecovery(
  value: unknown,
): PipefyAttachmentFieldRecovery | null {
  const raw = asRecord(value);
  if (!raw) return null;
  const outcome = raw.outcome;
  if (
    typeof outcome !== 'string' ||
    !(PIPEFY_ATTACHMENT_FIELD_RECOVERY_OUTCOMES as readonly string[]).includes(outcome)
  ) {
    return null;
  }
  const records = (entries: unknown): Record<string, unknown>[] =>
    Array.isArray(entries)
      ? entries
          .map((entry) => asRecord(entry))
          .filter((entry): entry is Record<string, unknown> => entry !== null)
      : [];

  return {
    fieldId: optionalString(raw.fieldId),
    ...(Array.isArray(raw.fieldIds)
      ? {
          fieldIds: raw.fieldIds.filter(
            (fieldId): fieldId is string =>
              typeof fieldId === 'string' && fieldId.trim().length > 0,
          ),
        }
      : {}),
    outcome: outcome as PipefyAttachmentFieldRecoveryOutcome,
    recovered: records(raw.recovered).flatMap((entry) =>
      typeof entry.cardId === 'string' &&
      typeof entry.fieldId === 'string' &&
      typeof entry.attachmentPath === 'string' &&
      typeof entry.blake3 === 'string'
        ? [
            {
              cardId: entry.cardId,
              fieldId: entry.fieldId,
              attachmentPath: entry.attachmentPath,
              blake3: entry.blake3,
            },
          ]
        : [],
    ),
    failures: records(raw.failures).flatMap((entry) =>
      typeof entry.attachmentPath === 'string' && typeof entry.reason === 'string'
        ? [{ attachmentPath: entry.attachmentPath, reason: entry.reason }]
        : [],
    ),
    ...(typeof raw.error === 'string' && raw.error ? { error: raw.error } : {}),
    ...(typeof raw.truncated === 'number' && Number.isInteger(raw.truncated) && raw.truncated > 0
      ? { truncated: raw.truncated }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Direct webhook intake (phase 1)
// ---------------------------------------------------------------------------

export const PIPEFY_INTEGRATION_ID = 'pipefy';
export const PIPEFY_WEBHOOK_ACTIONS = [
  'card.create',
  'card.field_update',
  'card.move',
  'card.delete',
] as const;

// ---------------------------------------------------------------------------
// Webhook/API intake (phase 6B)
// ---------------------------------------------------------------------------

/** Channel that ran the structured intake for a Pipefy card. */
export const PIPEFY_INTAKE_CHANNELS = ['EMAIL', 'WEBHOOK'] as const;
export type PipefyIntakeChannel = (typeof PIPEFY_INTAKE_CHANNELS)[number];

/** `PipefySyncOperation.operation` for the outbound IntegradoERP flag write. */
export const PIPEFY_MARK_INTEGRATED_ERP_OPERATION = 'MARK_INTEGRATED_ERP';
/** `PipefySyncOperation.operation` for a confirmed card ↔ bill association. */
export const PIPEFY_SYNC_BILL_ASSOCIATION_OPERATION = 'SYNC_BILL_ASSOCIATION';
/**
 * `PipefySyncOperation.operation` recorded when a payment is created on a correlated bill: the
 * date of the card's latest move to the approval or payment-day phase is applied to it.
 */
export const PIPEFY_APPLY_PAYMENT_TRIGGER_DATE_OPERATION = 'APPLY_PAYMENT_TRIGGER_DATE';
/** `PipefySyncOperation.source` for operations recorded when a payment is created. */
export const PIPEFY_PAYMENT_CREATED_SOURCE = 'AP_PAYMENT_CREATED';
/** `PipefySyncOperation.source` for flag writes issued after card ↔ payable correlation. */
export const PIPEFY_CORRELATION_SOURCE = 'PIPEFY_CARD_CORRELATION';

/** Order of the day and month components in slash-separated dates returned by the Pipefy API. */
export const PIPEFY_INTAKE_DATE_ORDERS = ['MDY', 'DMY'] as const;
export type PipefyIntakeDateOrder = (typeof PIPEFY_INTAKE_DATE_ORDERS)[number];

/**
 * Optional card-field mappings for the webhook/API intake. Each value is a Pipefy field id or
 * label from the configured pipe; the required mappings (taxId, amount, dueDate, vendor,
 * integratedErp) stay in `settings.fields`.
 */
export const PipefyIntakeFieldMappingsSchema = z
  .object({
    entity: z.string().min(1).optional(),
    requester: z.string().min(1).optional(),
    department: z.string().min(1).optional(),
    /**
     * One field, or one field per department when the pipe has a "Categoria - <Departamento>"
     * dropdown for each department. With a list, the card's department picks the field whose
     * label ends with that department; a single filled field is used when the department's own
     * field is empty, and several filled fields with none for the department yield no category.
     */
    category: z.union([z.string().min(1), z.array(z.string().min(1)).min(1).max(50)]).optional(),
    currency: z.string().min(1).optional(),
    scheduledDate: z.string().min(1).optional(),
    paymentMethod: z.string().min(1).optional(),
    pixKey: z.string().min(1).optional(),
    barcode: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    notes: z.string().min(1).optional(),
  })
  .strict();
export type PipefyIntakeFieldMappings = z.infer<typeof PipefyIntakeFieldMappingsSchema>;

/**
 * Maps a tenant-defined alias to any field in the selected Pipefy pipe. The alias is the stable
 * key used by intake rules; `field` may be either the provider field id or its label.
 */
export const PipefyCustomFieldMappingSchema = z
  .object({
    key: z
      .string()
      .trim()
      .min(1)
      .max(80)
      .regex(/[a-z0-9]/i, 'key must contain at least one letter or number'),
    field: z.string().trim().min(1).max(255),
  })
  .strict();
export type PipefyCustomFieldMapping = z.infer<typeof PipefyCustomFieldMappingSchema>;

export function normalizePipefyCustomFieldKey(value: string): string {
  return `custom_${value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')}`;
}

export const PipefyIntakeSettingsSchema = z
  .object({
    /**
     * Legacy persisted mirror of businessRulesExecutionEnabled. New configuration changes must
     * keep this value synchronized instead of exposing a second activation switch.
     */
    enabled: z.boolean().default(false),
    /**
     * EmailInbox that owns the intake envelope. It MUST be the inbox the Pipefy email automation
     * delivers to: both channels reserve the same `(tenant, source, cardId)` key, which is what
     * makes webhook and email mutually exclusive per card. The inbox is retained for routing.
     */
    inboxId: z.string().uuid(),
    /** Entity used when the card has no entity hint; falls back to the inbox default entity. */
    defaultEntityId: z.string().uuid().nullable().optional(),
    /** Also run the intake on `card.field_update` so attachments added later can be recovered. */
    reprocessOnFieldUpdate: z.boolean().default(true),
    /** Keep the email-compatible metadata-only path when no attachment is available. */
    requireAttachment: z.boolean().default(false),
    slashDateOrder: z.enum(PIPEFY_INTAKE_DATE_ORDERS).default('MDY'),
    /** Extra hostnames allowed for attachment downloads, on top of the inbox allow-list. */
    allowedAttachmentDomains: z.array(z.string().min(1)).default([]),
    fields: PipefyIntakeFieldMappingsSchema.default({}),
    /** Extra card fields exposed to intake rules under the `custom_<key>` raw-field key. */
    customFields: z
      .array(PipefyCustomFieldMappingSchema)
      .max(50)
      .superRefine((mappings, context) => {
        const seen = new Map<string, number>();
        mappings.forEach((mapping, index) => {
          const normalized = normalizePipefyCustomFieldKey(mapping.key);
          const previousIndex = seen.get(normalized);
          if (previousIndex !== undefined) {
            context.addIssue({
              code: 'custom',
              path: [index, 'key'],
              message: `custom field alias duplicates item ${previousIndex + 1}`,
            });
          } else {
            seen.set(normalized, index);
          }
        });
      })
      .default([]),
    /** Attachment field ids/labels; empty means every attachment-typed field on the card. */
    attachmentFields: z.array(z.string().min(1)).default([]),
  })
  .strict();
export type PipefyIntakeSettings = z.infer<typeof PipefyIntakeSettingsSchema>;

/** Partial intake settings sent by the setup screen; the API supplies runtime defaults. */
export const PipefyIntakeSettingsDraftSchema = PipefyIntakeSettingsSchema.partial().strict();
export type PipefyIntakeSettingsDraft = z.infer<typeof PipefyIntakeSettingsDraftSchema>;

/** Whether the intake created the payable or found it. */
export const PIPEFY_INTAKE_BILL_ORIGINS = ['CREATED', 'EXISTING'] as const;
export type PipefyIntakeBillOrigin = (typeof PIPEFY_INTAKE_BILL_ORIGINS)[number];

/**
 * Why an intake was scheduled from a webhook event.
 */
export const PIPEFY_INTAKE_TRIGGER_REASONS = [
  'CARD_CREATE',
  'CARD_MOVE',
  'FIELD_UPDATE',
  'INTAKE_DISABLED',
  'INTEGRATION_ORIGINATED',
  'PHASE_NOT_VALIDATED',
  'FIELD_UPDATE_DISABLED',
  'ACTION_NOT_ELIGIBLE',
] as const;
export type PipefyIntakeTriggerReason = (typeof PIPEFY_INTAKE_TRIGGER_REASONS)[number];

export const PIPEFY_CONFIG_SCOPES = ['TENANT', 'ENTITY'] as const;
export type PipefyConfigurationScope = (typeof PIPEFY_CONFIG_SCOPES)[number];

export const PIPEFY_BILL_LINK_BACKFILL_CURSOR_TYPES = ['EVENT', 'INTAKE'] as const;
export type PipefyBillLinkBackfillCursorType =
  (typeof PIPEFY_BILL_LINK_BACKFILL_CURSOR_TYPES)[number];

export interface PipefyBillLinkBackfillRequest {
  scopeType: PipefyConfigurationScope;
  entityId?: string | null;
  cursorType?: PipefyBillLinkBackfillCursorType;
  cursorId?: string;
}

export interface PipefyBillLinkBackfillResponse {
  scanned: number;
  queued: number;
  nextCursorType: PipefyBillLinkBackfillCursorType | null;
  nextCursorId: string | null;
}

export const PIPEFY_CONFIG_STATUSES = ['CONFIGURING', 'ACTIVE', 'INACTIVE', 'ERROR'] as const;
export type PipefyConfigurationStatus = (typeof PIPEFY_CONFIG_STATUSES)[number];

export const PIPEFY_CONNECTION_STATUSES = ['SUCCESS', 'FAILED'] as const;
export type PipefyConfigurationConnectionStatus = (typeof PIPEFY_CONNECTION_STATUSES)[number];

export const PIPEFY_PHASE_ACTIONS = ['IGNORE', 'PROCESS'] as const;
export type PipefyPhaseAction = (typeof PIPEFY_PHASE_ACTIONS)[number];

function validatePipefyBillLinkFieldMapping(
  fields: Record<string, unknown>,
  context: z.RefinementCtx,
): void {
  const billLinkFieldId = fields.billLink;
  if (typeof billLinkFieldId !== 'string') return;

  const conflictingMapping = Object.entries(fields).find(
    ([key, value]) => key !== 'billLink' && value === billLinkFieldId,
  );
  if (!conflictingMapping) return;

  context.addIssue({
    code: 'custom',
    path: ['billLink'],
    message: `Pipefy bill link field must be different from ${conflictingMapping[0]}`,
  });
}

export const PIPEFY_REQUIRED_FIELD_MAPPINGS = [
  'taxId',
  'amount',
  'dueDate',
  'vendor',
  'integratedErp',
] as const;
export type PipefyRequiredFieldMapping = (typeof PIPEFY_REQUIRED_FIELD_MAPPINGS)[number];

/**
 * Field mappings a company may configure but that never gate activation. There is
 * deliberately no default value: when a mapping is absent the integration must not
 * read any card field in its place.
 */
export const PIPEFY_OPTIONAL_FIELD_MAPPINGS = ['attachments', 'billLink'] as const;
export type PipefyOptionalFieldMapping = (typeof PIPEFY_OPTIONAL_FIELD_MAPPINGS)[number];

/** Pipefy discovery `type` values that identify an attachment field. */
export const PIPEFY_ATTACHMENT_FIELD_TYPES = ['attachment'] as const;

export function isPipefyAttachmentFieldType(type: string | null | undefined): boolean {
  if (!type) return false;
  return (PIPEFY_ATTACHMENT_FIELD_TYPES as readonly string[]).includes(type.trim().toLowerCase());
}

const PIPEFY_STABLE_FIELD_ID_MESSAGE =
  'Pipefy field mapping must be a stable field id, not a URL or attachment value';

/**
 * Stable Pipefy field identifier: a slug such as `anexos_boletos` or `cnpj_cpf`. Only
 * letters, digits, `_` and `-` are allowed, so signed attachment URLs, file names and other
 * transient values are rejected and a mapping always points at a field of the pipe rather
 * than at one card's file.
 */
export const PipefyStableFieldIdSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(/^[A-Za-z0-9_-]+$/, { message: PIPEFY_STABLE_FIELD_ID_MESSAGE });

export const PIPEFY_BUSINESS_RULE_KEYS = [
  'approvalTriggerPhase',
  'paymentDayPhase',
  'inProgressPhase',
  'paymentCompletedPhase',
  'analysisPhase',
  'cancelledPhase',
] as const;
export type PipefyBusinessRuleKey = (typeof PIPEFY_BUSINESS_RULE_KEYS)[number];

export const PIPEFY_REQUIRED_BUSINESS_RULE_KEYS = [
  'approvalTriggerPhase',
  'paymentDayPhase',
  'inProgressPhase',
  'paymentCompletedPhase',
  'analysisPhase',
] as const satisfies readonly Exclude<PipefyBusinessRuleKey, 'cancelledPhase'>[];

export interface PipefyBusinessRulePhase {
  /** Runtime integrations must use this provider phase id. */
  id: string;
  /** Display-only label captured from Pipefy discovery. */
  name: string;
}

export interface PipefyBusinessRules {
  approvalTriggerPhase: PipefyBusinessRulePhase;
  /** Optional on legacy persisted configurations; required when activating/saving setup. */
  paymentDayPhase?: PipefyBusinessRulePhase;
  inProgressPhase: PipefyBusinessRulePhase;
  paymentCompletedPhase: PipefyBusinessRulePhase;
  analysisPhase: PipefyBusinessRulePhase;
  cancelledPhase?: PipefyBusinessRulePhase;
}

const PipefyBusinessRulePhaseSchema = z
  .object({ id: z.string().min(1), name: z.string().min(1).max(255) })
  .strict();

const PipefyBusinessRulesBaseSchema = z
  .object({
    approvalTriggerPhase: PipefyBusinessRulePhaseSchema.nullable().optional(),
    paymentDayPhase: PipefyBusinessRulePhaseSchema.nullable().optional(),
    inProgressPhase: PipefyBusinessRulePhaseSchema.nullable().optional(),
    paymentCompletedPhase: PipefyBusinessRulePhaseSchema.nullable().optional(),
    analysisPhase: PipefyBusinessRulePhaseSchema.nullable().optional(),
    cancelledPhase: PipefyBusinessRulePhaseSchema.nullable().optional(),
  })
  .strict();

function validatePipefyBusinessRuleConflicts(
  rules: z.infer<typeof PipefyBusinessRulesBaseSchema>,
  context: z.RefinementCtx,
) {
  const selected = PIPEFY_BUSINESS_RULE_KEYS.flatMap((key) => {
    const phase = rules[key];
    return phase ? [{ key, id: phase.id }] : [];
  });
  const seen = new Map<string, string>();
  for (const { key, id } of selected) {
    const previousKey = seen.get(id);
    if (previousKey) {
      context.addIssue({
        code: 'custom',
        path: [key, 'id'],
        message: `Pipefy phase is already assigned to ${previousKey}`,
      });
    } else {
      seen.set(id, key);
    }
  }
}

export const PipefyBusinessRulesDraftSchema = PipefyBusinessRulesBaseSchema.superRefine(
  validatePipefyBusinessRuleConflicts,
);
export type PipefyBusinessRulesDraft = z.infer<typeof PipefyBusinessRulesDraftSchema>;

export const PipefyBusinessRulesSchema = PipefyBusinessRulesBaseSchema.extend({
  approvalTriggerPhase: PipefyBusinessRulePhaseSchema,
  inProgressPhase: PipefyBusinessRulePhaseSchema,
  paymentCompletedPhase: PipefyBusinessRulePhaseSchema,
  analysisPhase: PipefyBusinessRulePhaseSchema,
}).superRefine(validatePipefyBusinessRuleConflicts);

export const PipefyCompleteConfigSettingsSchema = z.object({
  pipe: z.object({
    id: z.string().min(1),
    name: z.string().min(1).max(255),
    suid: z.string().min(1).optional(),
  }),
  fields: z
    .object({
      taxId: z.string().min(1),
      amount: z.string().min(1),
      dueDate: z.string().min(1),
      vendor: z.string().min(1),
      integratedErp: z.string().min(1),
      attachments: PipefyStableFieldIdSchema.optional(),
      billLink: PipefyStableFieldIdSchema.optional(),
      // Legacy read compatibility. Rejection reasons are synchronized as comments.
      paymentRejectionReason: z.string().min(1).optional(),
    })
    .strict()
    .superRefine(validatePipefyBillLinkFieldMapping),
  phases: z.record(z.string().min(1), z.enum(PIPEFY_PHASE_ACTIONS)),
  events: z.array(z.enum(PIPEFY_WEBHOOK_ACTIONS)).min(1),
  /** Master switch for Pipefy financial/card side effects, including webhook bill intake. */
  businessRulesExecutionEnabled: z.boolean().default(false),
  businessRules: PipefyBusinessRulesSchema,
  /** Phase 6B webhook/API payable intake configuration; enabled mirrors the master switch. */
  intake: PipefyIntakeSettingsSchema.optional(),
});

export const PipefyConfigSettingsDraftSchema = z
  .object({
    pipe: z
      .object({
        id: z.string().min(1),
        name: z.string().min(1),
        suid: z.string().min(1).optional(),
      })
      .optional(),
    // A null draft value explicitly removes a previously saved optional mapping.
    fields: z
      .record(z.string(), z.string().min(1).nullable())
      .superRefine((fields, context) => {
        for (const key of PIPEFY_OPTIONAL_FIELD_MAPPINGS) {
          const fieldId = fields[key];
          if (fieldId === undefined || fieldId === null) continue;
          if (!PipefyStableFieldIdSchema.safeParse(fieldId).success) {
            context.addIssue({
              code: 'custom',
              path: [key],
              message: PIPEFY_STABLE_FIELD_ID_MESSAGE,
            });
          }
        }
      })
      .optional(),
    phases: z.record(z.string(), z.enum(PIPEFY_PHASE_ACTIONS)).optional(),
    events: z.array(z.enum(PIPEFY_WEBHOOK_ACTIONS)).min(1).optional(),
    businessRulesExecutionEnabled: z.boolean().optional(),
    businessRules: PipefyBusinessRulesDraftSchema.optional(),
    // A null draft value explicitly removes a previously saved intake block.
    intake: PipefyIntakeSettingsDraftSchema.nullable().optional(),
  })
  .strict();

export type PipefyConfigSettingsDraft = z.infer<typeof PipefyConfigSettingsDraftSchema>;

export interface PipefyConfigurationDraftRequest {
  scopeType: PipefyConfigurationScope;
  entityId?: string | null;
  credentials?: Record<string, unknown>;
  settings?: PipefyConfigSettingsDraft;
}

export const PipefyConfigScopeSchema = z.object({
  scopeType: z.enum(PIPEFY_CONFIG_SCOPES),
  entityId: z.string().uuid().nullable().optional(),
});

const PipefyPipeSettingsSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(255),
});

const PipefyFieldMappingsSchema = z
  .object({
    taxId: z.string().min(1),
    amount: z.string().min(1),
    dueDate: z.string().min(1),
    vendor: z.string().min(1),
    integratedErp: z.string().min(1),
    attachments: PipefyStableFieldIdSchema.optional(),
    billLink: PipefyStableFieldIdSchema.optional(),
    // Legacy read compatibility. Rejection reasons are synchronized as comments.
    paymentRejectionReason: z.string().min(1).optional(),
  })
  .strict()
  .superRefine(validatePipefyBillLinkFieldMapping);

const PipefyPhaseMappingsSchema = z.record(z.string().min(1), z.enum(PIPEFY_PHASE_ACTIONS));

export const PipefyConfigSettingsSchema = z.object({
  pipe: PipefyPipeSettingsSchema.extend({
    suid: z.string().min(1).optional(),
  }),
  fields: PipefyFieldMappingsSchema,
  phases: PipefyPhaseMappingsSchema,
  events: z.array(z.enum(PIPEFY_WEBHOOK_ACTIONS)).min(1),
  businessRulesExecutionEnabled: z.boolean().default(false),
  /** Optional only for backwards-compatible reads of pre-4A settings. */
  businessRules: PipefyBusinessRulesSchema.optional(),
  intake: PipefyIntakeSettingsSchema.optional(),
});

export type PipefyConfigSettings = z.infer<typeof PipefyConfigSettingsSchema>;

export interface PipefyDiscoveredField {
  id: string;
  label: string;
  /** Pipefy field type, when returned by the discovery API. */
  type?: string | null;
}

export interface PipefyDiscoveredPhase {
  id: string;
  name: string;
  fields: PipefyDiscoveredField[];
}

export interface PipefyDiscoveredPipe {
  id: string;
  suid: string | null;
  name: string;
  organizationId: string | null;
  phases: PipefyDiscoveredPhase[];
  fields: PipefyDiscoveredField[];
}

export interface PipefyDiscoveryResult {
  pipes: PipefyDiscoveredPipe[];
}

export interface PipefySafeError {
  code: string;
  message: string;
}

export type PipefyWebhookAction = (typeof PIPEFY_WEBHOOK_ACTIONS)[number];

const PipefyWebhookCardSchema = z
  .object({
    id: z.union([z.string().min(1), z.number().finite()]).optional(),
    pipe_id: z.union([z.string().min(1), z.number().finite()]).optional(),
    pipeId: z.union([z.string().min(1), z.number().finite()]).optional(),
  })
  .passthrough();

const pipefyWebhookEventShape = {
  action: z.string().min(1).optional(),
  event: z.string().min(1).optional(),
  event_type: z.string().min(1).optional(),
  eventType: z.string().min(1).optional(),
  card: PipefyWebhookCardSchema.optional(),
};

function validatePipefyWebhookEventData(
  value: Record<string, unknown>,
  context: z.RefinementCtx,
): void {
  const action = pipefyFirstString(value, ['action', 'event', 'event_type', 'eventType']);
  if (!action) {
    context.addIssue({ code: 'custom', message: 'Pipefy webhook action is missing' });
  }
  const card = asRecord(value.card);
  if (!card || !pipefyStringId(card.id)) {
    context.addIssue({ code: 'custom', message: 'Pipefy webhook card.id is missing' });
  }
}

const PipefyWebhookEventDataSchema = z
  .object(pipefyWebhookEventShape)
  .passthrough()
  .superRefine(validatePipefyWebhookEventData);

const PipefyWebhookEnvelopeSchema = z.object({ data: PipefyWebhookEventDataSchema }).passthrough();

const PipefyWebhookFlatSchema = z
  .object({ ...pipefyWebhookEventShape, data: z.undefined().optional() })
  .passthrough()
  .superRefine(validatePipefyWebhookEventData);

/** Provider fields remain open, but the event envelope, action and card reference are typed. */
export const PipefyWebhookPayloadSchema = z.union([
  PipefyWebhookEnvelopeSchema,
  PipefyWebhookFlatSchema,
]);

export interface PipefyWebhookEventInput {
  action: PipefyWebhookAction;
  cardId: string;
  pipeId: string | null;
  cardTitle: string | null;
  fieldId: string | null;
  newValue: unknown;
  fromPhase: Record<string, unknown> | null;
  toPhase: Record<string, unknown> | null;
  onPhase: Record<string, unknown> | null;
  responsibleUser: Record<string, unknown> | null;
  sourceEventId: string | null;
  sourceEventAt: Date | null;
  payload: Record<string, unknown>;
  actionData: Record<string, unknown>;
}

export interface PipefyWebhookAcceptedResponse {
  accepted: true;
  result: 'RECEIVED' | 'DUPLICATE';
  eventId: string;
  eventKey: string;
}

export interface PipefyWebhookConfiguration {
  allowedPipeIds: string[];
}

export function readPipefyWebhookConfiguration(value: unknown): PipefyWebhookConfiguration {
  const record = asRecord(value) ?? {};
  const rawPipeIds = Array.isArray(record.allowedPipeIds) ? record.allowedPipeIds : [];
  const allowedPipeIds = rawPipeIds
    .map(pipefyStringId)
    .filter((pipeId): pipeId is string => pipeId !== null);
  return { allowedPipeIds };
}

function pipefyStringId(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function pipefyFirstString(
  record: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return null;
}

function pipefyDate(value: unknown): Date | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function pipefyWebhookField(
  payloadRecord: Record<string, unknown>,
): Record<string, unknown> | null {
  const actionData = asRecord(payloadRecord.data) ?? payloadRecord;
  return asRecord(actionData.field) ?? asRecord(payloadRecord.field);
}

/**
 * Id of the card field changed by a `card.field_update` webhook, read from a stored (raw or
 * sanitized) payload. Null when the payload names no field.
 */
export function pipefyWebhookFieldId(payload: unknown): string | null {
  const payloadRecord = asRecord(payload);
  if (!payloadRecord) return null;
  const field = pipefyWebhookField(payloadRecord);
  return field ? pipefyFirstString(field, ['id', 'field_id', 'fieldId', 'internal_id']) : null;
}

/**
 * Extracts the stable card reference from the Pipefy webhook envelope while
 * keeping the raw payload open for provider fields added later.
 */
export function parsePipefyWebhookPayload(value: unknown): PipefyWebhookEventInput {
  const parsed = PipefyWebhookPayloadSchema.safeParse(value);
  if (!parsed.success) {
    const issue = parsed.error.issues.find(({ code }) => code === 'custom');
    throw new Error(issue?.message ?? 'Invalid Pipefy webhook payload');
  }

  const payload = parsed.data;
  const payloadRecord = asRecord(payload) ?? {};
  const actionData = asRecord(payloadRecord.data) ?? payloadRecord;
  const action =
    pipefyFirstString(actionData, ['action', 'event', 'event_type', 'eventType']) ??
    pipefyFirstString(payloadRecord, ['action', 'event', 'event_type', 'eventType']);
  if (!action) throw new Error('Pipefy webhook action is missing');
  if (!(PIPEFY_WEBHOOK_ACTIONS as readonly string[]).includes(action)) {
    throw new Error(`Unsupported Pipefy webhook action: ${action}`);
  }

  const card = asRecord(actionData.card) ?? asRecord(payloadRecord.card);
  const cardId = card ? pipefyStringId(card.id) : null;
  if (!cardId) throw new Error('Pipefy webhook card.id is missing');

  const pipe = asRecord(card?.pipe) ?? asRecord(actionData.pipe) ?? asRecord(payloadRecord.pipe);
  const pipeId =
    pipefyFirstString(card ?? {}, ['pipe_id', 'pipeId']) ??
    pipefyFirstString(actionData, ['pipe_id', 'pipeId']) ??
    pipefyFirstString(payloadRecord, ['pipe_id', 'pipeId']) ??
    pipefyFirstString(pipe ?? {}, ['id']);
  const sourceEventId =
    pipefyFirstString(payloadRecord, ['event_id', 'eventId', 'payload_id', 'payloadId', 'id']) ??
    pipefyFirstString(actionData, ['event_id', 'eventId', 'payload_id', 'payloadId', 'id']);
  const sourceEventAt = pipefyDate(
    pipefyFirstString(payloadRecord, [
      'timestamp',
      'event_timestamp',
      'occurred_at',
      'created_at',
      'createdAt',
    ]) ??
      pipefyFirstString(actionData, [
        'timestamp',
        'event_timestamp',
        'occurred_at',
        'created_at',
        'createdAt',
      ]),
  );

  const field = pipefyWebhookField(payloadRecord);
  const phase = asRecord(actionData.phase) ?? asRecord(payloadRecord.phase);
  const fromPhase =
    asRecord(actionData.from_phase) ??
    asRecord(actionData.fromPhase) ??
    asRecord(actionData.from) ??
    asRecord(payloadRecord.from_phase) ??
    asRecord(payloadRecord.from);
  const toPhase =
    asRecord(actionData.to_phase) ??
    asRecord(actionData.toPhase) ??
    asRecord(actionData.to) ??
    asRecord(payloadRecord.to_phase) ??
    asRecord(payloadRecord.to);
  const onPhase =
    asRecord(actionData.on_phase) ??
    asRecord(actionData.onPhase) ??
    asRecord(payloadRecord.on_phase) ??
    phase;
  const responsibleUser =
    asRecord(actionData.user) ??
    asRecord(actionData.responsible_user) ??
    asRecord(actionData.updated_by) ??
    asRecord(actionData.moved_by) ??
    asRecord(payloadRecord.user);

  return {
    action: action as PipefyWebhookAction,
    cardId,
    pipeId,
    cardTitle: pipefyFirstString(card ?? {}, ['title']),
    fieldId: pipefyFirstString(field ?? {}, ['id', 'field_id', 'fieldId']),
    newValue:
      actionData.new_value ??
      actionData.newValue ??
      field?.new_value ??
      field?.newValue ??
      payloadRecord.new_value ??
      payloadRecord.newValue ??
      null,
    fromPhase,
    toPhase,
    onPhase,
    responsibleUser,
    sourceEventId,
    sourceEventAt,
    payload: payloadRecord,
    actionData,
  };
}

/**
 * Comparison form for a Pipefy field label. The intake configuration may name a field by id or
 * by label, so both the card mapper (worker) and the activation checks (API) resolve a configured
 * value against labels through this single normalization.
 */
export function normalizePipefyFieldLabel(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Resolve a configured Pipefy field (id or label) to the discovered field id.
 * Shared by activation validation and the card mapper so both accept the same contract.
 */
export function resolvePipefyConfiguredFieldId(
  configured: string,
  fields: readonly { id: string; label?: string | null }[],
): string | null {
  const wanted = configured.trim();
  if (!wanted) return null;
  if (fields.some((field) => field.id === wanted)) return wanted;
  const normalized = normalizePipefyFieldLabel(wanted);
  const byLabel = fields.find(
    (field) => field.label != null && normalizePipefyFieldLabel(field.label) === normalized,
  );
  return byLabel?.id ?? null;
}

const SENSITIVE_PIPEFY_KEY =
  /(token|secret|password|authorization|api[-_]?key|credential|private[-_]?key|certificate|bearer|cookie|\bauth\b|cpf|cnpj|tax[_-]?id)/i;
const CPF_PATTERN = /\b\d{3}[.\s]?\d{3}[.\s]?\d{3}[-\s]?\d{2}\b/g;
const CNPJ_PATTERN = /\b\d{2}[.\s]?\d{3}[.\s]?\d{3}[/\s]?\d{4}[-\s]?\d{2}\b/g;

/** Sanitizes stored webhook evidence without retaining credentials or complete Brazilian tax ids. */
export function sanitizePipefyWebhookPayload(value: unknown, key?: string): unknown {
  if (key && SENSITIVE_PIPEFY_KEY.test(key)) return '[REDACTED]';
  if (typeof value === 'string') {
    const redacted = value
      .replace(CPF_PATTERN, '[REDACTED_CPF]')
      .replace(CNPJ_PATTERN, '[REDACTED_CNPJ]');
    return persistablePipefySnapshotValue(redacted);
  }
  if (Array.isArray(value)) return value.map((item) => sanitizePipefyWebhookPayload(item));
  const record = asRecord(value);
  if (!record) return value;
  return Object.fromEntries(
    Object.entries(record).map(([entryKey, entryValue]) => [
      entryKey,
      sanitizePipefyWebhookPayload(entryValue, entryKey),
    ]),
  );
}
